import {
  FormControl,
  FormControlLabel,
  Checkbox,
  Paper,
} from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import OutsideListener from './OutsideListener'
import effects from '../effects/effects'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import {
  formatHomelessGroup,
  sortGroupByType,
  ORDINARY_GROUP,
} from 'commons/groupLogic'
import * as R from 'ramda'
import { safePropEq } from 'libs/ramdaExt'
import { isEmptyOrNil } from 'libs/empty'
import FolderSharedOutlinedIcon from '@material-ui/icons/FolderSharedOutlined'

const getHomelessTagIds = R.curry((homelessGroupId, state) =>
  R.compose(
    R.defaultTo([]),
    R.flatten,
    R.map(R.compose(R.map(R.prop('id')), R.prop('tags'))),
    R.filter(safePropEq('id', homelessGroupId)),
    R.prop('groups'),
  )(state),
)
const contains = (list) => (one) =>
  R.compose(
    R.cond([
      [isEmptyOrNil, R.F],
      [() => isEmptyOrNil(list), R.F],
      [R.T, R.includes(R.__, list)],
    ]),
  )(one)

const ifSelectSpecialToEmpty = R.curry((homeLessTagIds, state) =>
  R.compose(
    R.defaultTo([]),
    R.ifElse(
      //Current slectedTags select homeless group
      R.compose(R.not, R.isEmpty, R.intersection(homeLessTagIds)),
      R.always([]),
      R.filter(R.complement(contains(homeLessTagIds))), //filter-out homeless
    ),
    R.prop('selectedTags'),
  )(state),
)

const useStyle = makeStyles((theme) => ({
  inputSelectForm: {
    width: '100%',
  },
  tagsSelectLabel: {
    background: 'white',
    padding: '0 4px',
    top: '-26px',
    fontSize: '10px',
    transform: 'translate(14px, 20px) scale(1) !important',
    color: 'rgba(0, 0, 0, 0.54) !important',
  },
  tagsSelect: {
    padding: '18.5px 14px',
  },
  tagsSelectValues: {
    width: '300px !important',
    position: 'absolute',
    bottom: '-1px',
    transform: 'translateY(100%)',
    left: '0',
    right: '0',
    zIndex: '100',
  },
  tagsSelectListContainer: {
    overflow: 'auto',
    maxHeight: '455px',
  },
  fullWidth: {
    width: '100%',
  },
  groupCount: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    border: 'solid 1px #ffffff',
    marginLeft: 22,
    color: '#00e0ff',
    fontSize: 14,
    padding: '5px 9px 5px 15px',
    cursor: 'pointer',
    '& span': {
      marginLeft: 9,
    },
  },
}))

export default (props) => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const fetchGroups = async () => {
    try {
      const groups = R.compose(
        R.map(formatHomelessGroup(formatMessage)),
        R.propOr([], 'data'),
      )(
        await effects.apis.groupsWithTags({
          limit: 10000,
          types: [ORDINARY_GROUP],
        }),
      )
      setState({
        ...state,
        count: generateCount(state.selectedTags, groups),
        groups,
      })
    } catch (e) {
      dispatch(
        MessageAlert.showError({
          message: formatMessage({ id: 'wrong.msg' }),
        }),
      )
    }
  }
  let specialTypeId = {}

  const { selectedTags, onTagsChange, disabled, wrapperClass = '' } = props
  const classes = useStyle()

  const generateCount = (selectedTags, groups = state.groups) => {
    const allTagNames = []
    groups.forEach((group) => {
      group.tags &&
        group.tags.forEach((tag) => {
          selectedTags.forEach((tagId) => {
            if (tag.id === tagId) {
              allTagNames.push(tag.name)
            }
          })
        })
    })

    return allTagNames.length
  }

  const [state, setState] = useState({
    isOpenPaper: false,
    count: '',
    selectedTags: [...selectedTags],
    groups: [],
  })

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchGroups()
  }, [])
  /* eslint-enable react-hooks/exhaustive-deps */

  const isGroupChecked = (id) => {
    const currentGroup = state.groups.find((group) => group.id === id)
    let isChecked = true
    currentGroup.tags.forEach((tag) => {
      isChecked = isChecked && state.selectedTags.indexOf(tag.id) !== -1
    })
    return isChecked
  }

  const handleGroupCheck = (id) => {
    const tagIds = state.groups
      .find((group) => group.id === id)
      .tags.map((tag) => tag.id)

    if (isGroupChecked(id)) {
      let ids = state.selectedTags.filter((_id) => tagIds.indexOf(_id) === -1)

      setState({
        ...state,
        selectedTags: ids,
        count: generateCount(
          state.selectedTags.filter((_id) => tagIds.indexOf(_id) === -1),
        ),
      })

      onTagsChange(ids)
    } else {
      let ids = []

      if (specialTypeId === id) {
        ids = [...new Set([...tagIds])]
      } else {
        /**/
        const selectedTags = ifSelectSpecialToEmpty(
          getHomelessTagIds(specialTypeId, state),
          state,
        )
        /**/

        ids = [...new Set([...selectedTags, ...tagIds])]
      }

      setState({
        ...state,
        selectedTags: ids,
        count: generateCount(ids),
      })
      onTagsChange(ids)
    }
  }

  const handleChange = (id) => {
    let newSelectedTags
    if (state.selectedTags.indexOf(id) !== -1) {
      newSelectedTags = state.selectedTags.filter((_id) => _id !== id)
      setState({
        ...state,
        count: generateCount(newSelectedTags),
        selectedTags: newSelectedTags,
      })
    } else {
      const selectedTags = ifSelectSpecialToEmpty(
        getHomelessTagIds(specialTypeId, state),
        state,
      )

      newSelectedTags = [...selectedTags, id]
      let labelNames = generateCount(newSelectedTags)

      setState({
        ...state,
        count: labelNames,
        selectedTags: newSelectedTags,
      })
    }
    onTagsChange(newSelectedTags)
  }

  const setPaperState = (status) => {
    if (disabled) return
    setState({
      ...state,
      isOpenPaper: status,
    })
  }

  return (
    <OutsideListener
      onClickOutside={() => setPaperState(false)}
      className={wrapperClass}
    >
      <FormControl variant="outlined" className={classes.inputSelectForm}>
        <div
          className={classes.groupCount}
          onClick={() => setPaperState(!state.isOpenPaper)}
        >
          <FolderSharedOutlinedIcon />
          <span>
            {formatMessage(
              {
                id: !state.count
                  ? 'crm.noSubgroup.label'
                  : 'crm.subgroup.label',
              },
              {
                count: state.count,
              },
            )}
          </span>
        </div>

        {state.isOpenPaper && (
          <Paper elevation={1} className={classes.tagsSelectValues}>
            <div className={clsx('mx-16', classes.tagsSelectListContainer)}>
              {state.groups &&
                sortGroupByType(state.groups).map((group) => (
                  <div key={group.id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isGroupChecked(group.id)}
                          onChange={() => handleGroupCheck(group.id)}
                          value="checkedB"
                          color="primary"
                        />
                      }
                      label={group.name}
                    />
                    <div className="flex flex-col ltr:ml-16 rtl:mr-16">
                      {group.type !== 0
                        ? ((specialTypeId = group.id), '')
                        : group.tags &&
                          group.tags.map((tag) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    state.selectedTags.indexOf(tag.id) !== -1
                                  }
                                  onChange={() => handleChange(tag.id)}
                                  value="checkedB"
                                  color="primary"
                                />
                              }
                              label={tag.name}
                              key={tag.id}
                            />
                          ))}
                    </div>
                  </div>
                ))}
            </div>
          </Paper>
        )}
      </FormControl>
    </OutsideListener>
  )
}
